import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { claimsAPI } from '../services/api';
import ClaimStatusBadge from '../components/Claims/ClaimStatusBadge';

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const formatCurrency = (n) => n != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) : '—';

export default function Claims() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', claim_type: '' });
  const [stats, setStats] = useState({ active: 0, underReview: 0, pendingAmount: 0, paidCount: 0 });

  useEffect(() => {
    loadClaims();
  }, [filter.status, filter.claim_type]);

  async function loadClaims() {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.claim_type) params.claim_type = filter.claim_type;
      const list = await claimsAPI.list(params);
      setClaims(Array.isArray(list) ? list : []);

      const all = Array.isArray(list) ? list : [];
      const active = all.filter(c => ['draft', 'submitted'].includes(c.status)).length;
      const underReview = all.filter(c => c.status === 'under_review').length;
      const pendingAmount = all
        .filter(c => ['submitted', 'under_review', 'approved'].includes(c.status))
        .reduce((sum, c) => sum + (parseFloat(c.claimed_amount) || 0), 0);
      const paidCount = all.filter(c => c.status === 'paid').length;
      setStats({ active, underReview, pendingAmount, paidCount });
    } catch (e) {
      console.warn(e);
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">Claims Dashboard</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/claims/submit')}>
          Submit Claim
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Active Claims</Typography>
            <Typography variant="h6">{stats.active}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Under Review</Typography>
            <Typography variant="h6">{stats.underReview}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Pending Amount</Typography>
            <Typography variant="h6">{formatCurrency(stats.pendingAmount)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Paid This Period</Typography>
            <Typography variant="h6">{stats.paidCount}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filter.status} label="Status" onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="submitted">Submitted</MenuItem>
              <MenuItem value="under_review">Under Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select value={filter.claim_type} label="Type" onChange={(e) => setFilter(f => ({ ...f, claim_type: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="medical">Medical</MenuItem>
              <MenuItem value="dental">Dental</MenuItem>
              <MenuItem value="optical">Optical</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="subtitle2" color="textSecondary" gutterBottom>Recent Claims</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : claims.length === 0 ? (
          <Typography color="textSecondary">No claims found. Submit a claim to get started.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Claim #</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Service Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claims.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.claim_number}</TableCell>
                    <TableCell>{c.claim_type}</TableCell>
                    <TableCell>{c.service_provider || '—'}</TableCell>
                    <TableCell>{formatCurrency(c.claimed_amount)}</TableCell>
                    <TableCell><ClaimStatusBadge status={c.status} /></TableCell>
                    <TableCell>{formatDate(c.service_date)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => navigate(`/claims/${c.id}`)}>View Details</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <Button sx={{ mt: 2 }} onClick={() => navigate('/wallet')}>Back to Wallet</Button>
    </Container>
  );
}
